/* eslint-disable complexity */
import {
  Badge,
  Box,
  DialogTitle,
  Stack,
  Tab,
  Tabs,
  Typography
} from '@mui/material'
import React, { FC, useCallback, useContext, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import useAccessTokenAndShopId from '../../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { RO_CLOSED } from '../../../models/Status'
import { RepairOrderContext } from '../../../providers/RepairOrderProvider/RepairOrderProvider'
import { SubmitDialogContext } from '../../../providers/SubmitDialogProvider/SubmitDialogProvider'
import {
  useGetRepairOrdersQuery,
  useGetServiceItemsQuery
} from '../../../redux/api'
import {
  RepairOrderExpanded,
  ServiceExpanded,
  ServiceInfoStatusEnum
} from '../../../sdk'
import { LaborAndPartsStepper } from '../ServiceStepper/LaborAndPartsStepper'
import { PreviousServices } from './PreviousServices'
import { SearchCannedJobOrMotor } from './SearchCannedJobOrMotor'
import { SearchCustom } from './SearchCustom'
import {
  INITIAL_SELECTED,
  ModifiedCannedJobExpanded,
  SelectedJob
} from './transformSelectedJob'

enum ServiceTypes {
  canned = 'Canned Job',
  search = 'Labor Guide',
  custom = 'Custom',
  service = 'Previous',
  defer = 'Deferred'
}

interface Props {
  cancelCallback: () => void
  submitCallback: (serviceIds: string[]) => void
}

export interface SearchServiceProps {
  selected: SelectedJob
  setSelected: (s: SelectedJob) => void
  cancelCallback: () => void
  submitCallback: (serviceIds: string[]) => void
  filteredRepairOrders: RepairOrderExpanded[] | undefined
  loadingRepairOrders: boolean
  services: ServiceExpanded[] | undefined
  loadingServices: boolean
}

export const LookupServiceWindow: FC<Props> = ({
  cancelCallback,
  submitCallback
}) => {
  const { repairOrder } = useContext(RepairOrderContext)

  const [serviceType, setServiceType] = useState<ServiceTypes>(
    ServiceTypes.canned
  )
  const [selected, setSelected] = useState<SelectedJob>(INITIAL_SELECTED)

  const handleChangeType = useCallback(
    (event: React.SyntheticEvent, newValue: ServiceTypes) => {
      setSelected(INITIAL_SELECTED)
      setServiceType(newValue)
    },
    []
  )

  const { accessToken, shopId } = useAccessTokenAndShopId()

  /* previous and deferred start */
  const vehicleId = repairOrder!.entry!.info!.vehicle!

  const { data: repairOrders, isLoading: loadingRepairOrders } =
    useGetRepairOrdersQuery({
      authorization: accessToken,
      shop: shopId,
      vehicle: vehicleId
    })

  const { id } = useParams()

  const filteredRepairOrders = repairOrders
    ? repairOrders.filter(
        (r) => r.entry!.id !== id && r.entry!.info!.status === RO_CLOSED
      )
    : undefined

  const { data: services, isLoading: loadingServices } =
    useGetServiceItemsQuery(
      {
        authorization: accessToken,
        shop: shopId,
        repairOrder: (filteredRepairOrders || []).map((r) => r.entry!.id!)
      },
      { skip: !filteredRepairOrders || filteredRepairOrders.length === 0 }
    )

  // keep track of number of deferred services
  const deferredCount = useMemo(() => {
    if (!loadingServices && services) {
      const deferredServices = services
        .filter((s) => s.entry!.info!.recommendInFutureRepairOrders)
        .filter((s) => s.entry!.info!.status === ServiceInfoStatusEnum.Deferred)
      return deferredServices.length
    }
    return 0
  }, [loadingServices, services])

  // keep track of number of previous services
  const previousCount = useMemo(() => {
    if (!loadingServices && services) {
      const previousServices = services
        .filter((s) => s.entry!.info!.recommendInFutureRepairOrders)
        .filter(
          (s) => s.entry!.info!.status === ServiceInfoStatusEnum.Authorized
        )
      return previousServices.length
    }
    return 0
  }, [loadingServices, services])

  /* previous and deferred end */

  const [stepperProps, setStepperProps] = useState<
    | {
        services: ServiceExpanded[]
        cannedJob: ModifiedCannedJobExpanded[]
      }
    | undefined
  >()
  const { setIsSubmitting } = useContext(SubmitDialogContext)

  if (stepperProps) {
    return (
      <LaborAndPartsStepper
        {...stepperProps}
        submitCallback={async () => {
          setIsSubmitting(true)
          await submitCallback(stepperProps.services.map((s) => s.entry!.id!))
          setIsSubmitting(false)
        }}
        cancelCallback={cancelCallback}
      />
    )
  }

  const searchServiceProps: SearchServiceProps = {
    cancelCallback,
    submitCallback,
    selected,
    setSelected,
    filteredRepairOrders,
    loadingRepairOrders,
    services,
    loadingServices
  }
  const serviceTypeContent = () => {
    switch (serviceType) {
      case ServiceTypes.custom:
        return <SearchCustom {...searchServiceProps} />
      case ServiceTypes.service:
      case ServiceTypes.defer:
        return (
          <PreviousServices
            defer={serviceType === ServiceTypes.defer}
            {...searchServiceProps}
          />
        )
      case ServiceTypes.canned:
      case ServiceTypes.search:
        return (
          <SearchCannedJobOrMotor
            cannedJobOnly={serviceType === ServiceTypes.canned}
            setStepperProps={setStepperProps}
            {...searchServiceProps}
          />
        )
    }
  }
  return (
    <>
      <DialogTitle data-testid="dialogTitle">
        Search for Service Items
      </DialogTitle>
      <Box mx={2} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={serviceType} onChange={handleChangeType}>
          {Object.values(ServiceTypes).map((type) => {
            const count =
              type === ServiceTypes.service
                ? previousCount
                : type === ServiceTypes.defer
                ? deferredCount
                : ''
            return (
              <Tab
                key={type}
                label={
                  <Stack direction="row" spacing={type.length / 5}>
                    <Typography>{type}</Typography>
                    {count ? (
                      <Badge badgeContent={count} color="primary" />
                    ) : null}
                  </Stack>
                }
                value={type}
                data-testid={`serviceItem${type}Tab`}
              />
            )
          })}
        </Tabs>
      </Box>
      {serviceTypeContent()}
    </>
  )
}
